import React from "react";
import { FaBell, FaCircle } from "react-icons/fa";
import { useEffect, useRef, useState, useContext } from "react";
import styles from "./NotificationComponent.module.css";
import authContext from "../../auth/auth-context";
import axios from "axios";
import { formatDistanceToNow, isValid, parseISO } from "date-fns";


function formatCreatedAt(iso) {
  const d = parseISO(iso);
  return isValid(d) ? formatDistanceToNow(d, { addSuffix: true }) : iso;
}

const AppHeaderNotification = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [, setTimeTick] = useState(0);
  const containerRef = useRef(null);

  const authCtx = useContext(authContext);

  const acting = authCtx.selectedGroup;
  const headers = {
    "Acting-as": 'dealer',
    Authorization: `JWT ${authCtx?.token}`,
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  const markAsRead = (id) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, read: true } : m)),
    );
  };

  const markAllAsRead = () => {
    setMessages((prev) => prev.map((m) => ({ ...m, read: true })));
  };

  const formatMessages = (msgs) => {
    if (!Array.isArray(msgs)) return [];
    return msgs
      .map((msg) => ({
        id: msg.id,
        title: msg.associated_template.title,
        body: msg.associated_template.html_message,
        created_at: msg.created_at,
        read: msg.viewed,
      }))
      .sort((a, b) => {
        const da = parseISO(a.created_at);
        const db = parseISO(b.created_at);
        const aOk = isValid(da);
        const bOk = isValid(db);
        if (!aOk && !bOk) return 0;
        if (!aOk) return 1;
        if (!bOk) return -1;
        return db.getTime() - da.getTime();
      });
  };

  useEffect(() => {
    const userPk = authCtx?.userId;
    if (userPk == null) return;

    const fetchMessages = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/info-messages/by-user/${userPk}/`,
          { headers },
        );
        setMessages(formatMessages(response.data));
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    void fetchMessages();
    const intervalId = setInterval(fetchMessages, 5000);
    return () => clearInterval(intervalId);
  }, [authCtx?.userId, authCtx?.token]);

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setTimeTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMessageClick = async (msg) => {
    if (msg.read) return;
    markAsRead(msg.id);
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/info-messages/${msg.id}/mark-as-read/`,
        {},
        { headers },
      );
    } catch (err) {
      console.error(`Error marking message ${msg.id} as read:`, err);
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, read: false } : m)),
      );
    }
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <button
        className={styles.button}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Notifications"
      >
        <FaBell className={unreadCount > 0 ? styles.bellActive : styles.bell} />
        {unreadCount > 0 && (
          <span className={styles.badge}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>
              Notifications
              {unreadCount > 0 && (
                <span className={styles.headerCount}>{unreadCount} new</span>
              )}
            </span>
            {unreadCount > 0 && (
              <button className={styles.markAll} onClick={markAllAsRead}>
                Mark all as read
              </button>
            )}
          </div>

          <ul className={styles.list}>
            {messages.length === 0 ? (
              <li className={styles.empty}>
                <FaBell className={styles.emptyIcon} />
                <span>You&apos;re all caught up</span>
              </li>
            ) : (
              messages.map((msg) => (
                <li
                  key={msg.id}
                  className={`${styles.item} ${msg.read ? styles.itemRead : styles.itemUnread}`}
                  onClick={() => handleMessageClick(msg)}
                >
                  <div className={styles.itemTop}>
                    <span className={styles.itemTitle}>{msg.title}</span>
                    {!msg.read && <FaCircle className={styles.unreadDot} />}
                  </div>
                  <p
                    className={styles.itemBody}
                    dangerouslySetInnerHTML={{ __html: msg.body }}
                  />
                  <span className={styles.itemTime} title={msg.created_at}>
                    {formatCreatedAt(msg.created_at)}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AppHeaderNotification;
